import type { FlowInfo } from "../types/flow";
import type { RuntimeLogger } from "../runtime/types";
import { StateStore } from "./state-store";

export interface NavTarget {
 kind: "screen" | "route" | "back" | "external";
 to?: string;
 params?: Record<string, unknown>;
 replace?: boolean;
 url?: string;
}

export interface AuthCredentials {
 userId: string;
 accessToken: string;
 refreshToken?: string;
 isGuest?: boolean;
 expiresAt?: number;
}

export interface StorageAdapter {
 read(key: string): Promise<string | null> | string | null;
 write(key: string, value: string): Promise<void> | void;
 delete(key: string): Promise<void> | void;
}

export interface AuthAdapter {
 read(): AuthCredentials | null;
 write(creds: AuthCredentials): void;
 clear(): void;
}

export interface NavigationAdapter {
 navigate(target: NavTarget): void;
}

export interface RuntimeShellContext {
 title: string;
 description: string;
 icon: string;
}

export interface RuntimeRouteMeta {
 shell: RuntimeShellContext;
}

/**
 * Route metadata for the current screen.
 *
 * `slug` is the active screen slug, while `params` mirrors the current
 * route params exposed by the caller (e.g. `/chat/[sessionId]`). `query`
 * mirrors the current URL search params so flows can read
 * `@route.query.<key>` without the renderer needing to interpolate them
 * into `params`. Shell presentation is resolved from the active
 * URL-backed screen manifest.
 */
export interface RuntimeRouteContext {
 slug: string;
 params: Record<string, unknown>;
 query?: Record<string, unknown>;
 screenId?: string;
 shell?: RuntimeShellContext;
 meta?: RuntimeRouteMeta;
}

export interface UIAdapter {
 toast?(message: string, kind?: "info" | "success" | "warning" | "error"): void;
 focus?(fieldId: string): void;
 scroll?(target: string | number): void;
}

export interface CallTransport {
 call(
 operationId: string,
 payload: Record<string, unknown>,
 options?: { signal?: AbortSignal },
 ): Promise<{ ok: boolean; data: unknown; error?: string; status?: number }>;
}

export interface StreamTransport {
 stream(
 operationId: string,
 payload: Record<string, unknown>,
 options?: { signal?: AbortSignal },
 ): AsyncIterable<{ event: string; data: unknown }>;
}

export interface ExecutionContextOptions {
 flow: FlowInfo;
 args?: Record<string, unknown>;
 initialForm?: Record<string, unknown>;
 state?: unknown;
 session?: Record<string, unknown>;
 params?: Record<string, unknown>;
 route?: RuntimeRouteContext;
 resolveFlow?: (id: string) => FlowInfo | undefined;
 navigation: NavigationAdapter;
 storage?: StorageAdapter;
 auth?: AuthAdapter;
 ui?: UIAdapter;
 transport: CallTransport;
 streamTransport?: StreamTransport;
 logger?: RuntimeLogger;
 signal?: AbortSignal;
 event?: Record<string, unknown>;
}

interface StoreLike {
 get(scope: string): unknown;
 getAll?: () => Record<string, unknown>;
 set(scope: string, value: unknown): void;
}

function ensureStore(input: unknown): StoreLike {
 if (
 input &&
 typeof input === "object" &&
 typeof (input as StoreLike).get === "function" &&
 typeof (input as StoreLike).set === "function"
 ) {
 return input as StoreLike;
 }
 return new StateStore();
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
 return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function cloneRecord(value: unknown): Record<string, unknown> {
 if (!isPlainObject(value)) return {};
 return { ...value };
}

function readPathValue(root: unknown, segments: readonly string[]): unknown {
 let cur: unknown = root;
 for (const segment of segments) {
 if (cur === null || cur === undefined || typeof cur !== "object") return undefined;
 cur = (cur as Record<string, unknown>)[segment];
 }
 return cur;
}

function setPathImmutable(root: Record<string, unknown>, segments: readonly string[], value: unknown): Record<string, unknown> {
 if (segments.length ===0) return { ...root };
 const [head, ...rest] = segments;
 if (!head) return { ...root };
 const nextRoot = { ...root };
 if (rest.length ===0) {
 nextRoot[head] = value;
 return nextRoot;
 }
 nextRoot[head] = setPathImmutable(cloneRecord(root[head]), rest, value);
 return nextRoot;
}

function deletePathImmutable(root: Record<string, unknown>, segments: readonly string[]): Record<string, unknown> {
 if (segments.length ===0) return { ...root };
 const [head, ...rest] = segments;
 if (!head) return { ...root };
 const nextRoot = { ...root };
 if (rest.length ===0) {
 delete nextRoot[head];
 return nextRoot;
 }
 const childValue = root[head];
 if (!isPlainObject(childValue)) return nextRoot;
 nextRoot[head] = deletePathImmutable(childValue, rest);
 return nextRoot;
}

export class ExecutionContext {
 readonly flow: FlowInfo;
 readonly state: StoreLike;
 readonly storage: StorageAdapter | null;
 readonly auth: AuthAdapter | null;
 readonly ui: UIAdapter | null;
 readonly navigation: NavigationAdapter;
 readonly transport: CallTransport;
 readonly streamTransport: StreamTransport | null;
 readonly logger: RuntimeLogger;
 readonly signal: AbortSignal | null;
 readonly resolveFlow: (id: string) => FlowInfo | undefined;
 readonly route: RuntimeRouteContext;

 private input: Record<string, unknown>;
 private params: Record<string, unknown>;
 private session: Record<string, unknown>;
 private form: Record<string, unknown>;
 private result: unknown = undefined;
 private error: unknown = undefined;
 private event: unknown = undefined;
 private env: Record<string, unknown>;
 private pref: Record<string, unknown>;

 constructor(options: ExecutionContextOptions) {
 this.flow = options.flow;
 this.state = ensureStore(options.state);
 this.storage = options.storage ?? null;
 this.auth = options.auth ?? null;
 this.ui = options.ui ?? null;
 this.navigation = options.navigation;
 this.transport = options.transport;
 this.streamTransport = options.streamTransport ?? null;
 this.logger = options.logger ?? noopLogger;
 this.signal = options.signal ?? null;
 this.resolveFlow = options.resolveFlow ?? (() => undefined);

 this.input = options.args ? { ...options.args } : {};
 this.params = options.params ? { ...options.params } : {};
 const authCreds = options.auth?.read();
 this.session = options.session ?? (authCreds ? { ...(authCreds as unknown as Record<string, unknown>) } : {});
 const routeParams = options.route?.params ?? this.params;
 this.route = {
 ...(options.route ?? {}),
 slug: typeof options.route?.slug === "string" ? options.route.slug : "",
 params: { ...routeParams },
 ...(options.route?.query ? { query: { ...options.route.query } } : {}),
 ...(options.route?.shell ? { shell: { ...options.route.shell } } : {}),
 ...(options.route?.meta?.shell
 ? { meta: { shell: { ...options.route.meta.shell } } }
 : {}),
 };
 this.form = options.initialForm ? { ...options.initialForm } : {};
 this.event = options.event ? { ...options.event } : undefined;
 this.env = {};
 this.pref = {};
 }

 getScope(scope: string): Record<string, unknown> | undefined {
 switch (scope) {
 case "state": return this.state.getAll?.() ?? {};
 case "route": return this.route as unknown as Record<string, unknown>;
 case "session": return this.session;
 case "input":
 case "args": return this.input;
 case "params": return this.params;
 case "form": return this.form;
 case "env": return this.env;
 case "pref":
 case "prefs": return this.pref;
 case "result": return { v: this.result };
 case "error": return this.error as Record<string, unknown> | undefined;
 case "event": return this.event as Record<string, unknown> | undefined;
 default: {
 const flat = this.state.get(scope);
 return flat && typeof flat === "object" ? flat as Record<string, unknown> : undefined;
 }
 }
 }

 setSession(next: Record<string, unknown> | null): void { this.session = next ? { ...next } : {}; }
 setInput(next: Record<string, unknown> | null): void { this.input = next ? { ...next } : {}; }
 setParams(next: Record<string, unknown> | null): void { this.params = next ? { ...next } : {}; }
 setResult(value: unknown): void { this.result = value; }
 setError(err: unknown): void { this.error = err; }
 setEvent(payload: unknown): void { this.event = payload; }
 clearEvent(): void { this.event = undefined; }

 setForm(field: string, value: unknown): void {
 if (Object.is(this.form[field], value)) return;
 this.form = { ...this.form, [field]: value };
 const currentForm = this.state.get("form");
 this.state.set("form", isPlainObject(currentForm) ? { ...currentForm, [field]: value } : { [field]: value });
 }

 writeState(scope: string, segments: readonly string[], value: unknown): void {
 if (scope === "state") {
 if (segments.length ===0) return;
 const [slotName, ...rest] = segments;
 if (!slotName) return;
 if (rest.length ===0) {
 if (!Object.is(this.state.get(slotName), value)) this.state.set(slotName, value);
 return;
 }
 const existing = this.state.get(slotName);
 if (!Object.is(readPathValue(existing, rest), value)) this.state.set(slotName, setPathImmutable(cloneRecord(existing), rest, value));
 return;
 }
 if (scope === "session") {
 if (!Object.is(readPathValue(this.session, segments), value)) {
 writeInto(this.session, segments, value);
 this.state.set("session", { ...this.session });
 }
 return;
 }
 if (scope === "form") {
 if (!Object.is(readPathValue(this.form, segments), value)) writeInto(this.form, segments, value);
 return;
 }
 if (scope === "pref" || scope === "prefs") {
 if (!Object.is(readPathValue(this.pref, segments), value)) {
 writeInto(this.pref, segments, value);
 this.state.set("prefs", { ...this.pref });
 }
 return;
 }
 if (segments.length ===0) {
 if (!Object.is(this.state.get(scope), value)) this.state.set(scope, value);
 return;
 }
 const existing = this.state.get(scope);
 if (!Object.is(readPathValue(existing, segments), value)) this.state.set(scope, setPathImmutable(cloneRecord(existing), segments, value));
 }

 deleteState(scope: string, segments: readonly string[]): void {
 if (scope === "state") {
 if (segments.length ===0) return;
 const [slotName, ...rest] = segments;
 if (!slotName) return;
 if (rest.length ===0) {
 if (this.state.get(slotName) !== undefined) this.state.set(slotName, undefined);
 return;
 }
 const existing = this.state.get(slotName);
 if (readPathValue(existing, rest) !== undefined && isPlainObject(existing)) this.state.set(slotName, deletePathImmutable(existing, rest));
 return;
 }
 if (segments.length ===0) {
 if (this.state.get(scope) !== undefined) this.state.set(scope, undefined);
 return;
 }
 const existing = this.state.get(scope);
 if (readPathValue(existing, segments) !== undefined && isPlainObject(existing)) this.state.set(scope, deletePathImmutable(existing, segments));
 }
}

function writeInto(root: Record<string, unknown>, segments: readonly string[], value: unknown): void {
 if (segments.length ===0) return;
 let cur = root;
 for (let i =0; i< segments.length -1; i +=1) {
 const segment = segments[i] as string;
 if (!cur[segment] || typeof cur[segment] !== "object" || Array.isArray(cur[segment])) cur[segment] = {};
 cur = cur[segment] as Record<string, unknown>;
 }
 cur[segments[segments.length -1] as string] = value;
}

const noopLogger: RuntimeLogger = { debug: () => {}, info: () => {}, warn: () => {}, error: () => {} };